/*
 * LocalDB - lightweight IndexedDB-backed key/value store for UI preferences and notes.
 *
 * Features:
 * - JSON-serializable values by string keys.
 * - Persistent across sessions using IndexedDB.
 * - Compile-time collection definitions with optional descriptions.
 * - Basic ops: get, set, delete, clear (per collection or all).
 * - List collections and dump all entries within a collection.
 * - Export entire DB contents for backup/inspection.
 */

import { localDBSchema } from './schema';

export type JsonValue = null | boolean | number | string | JsonValue[] | { [k: string]: JsonValue };

export type CollectionName = keyof typeof localDBSchema & string;

export interface CollectionDef {
	description?: string;
	keys: Record<string, { description?: string }>;
}

export type CollectionsSchema = typeof localDBSchema;

interface KVRecord {
	key: string; // composed as `${collection}:${userKey}`
	collection: string;
	userKey: string;
	value: JsonValue;
	updatedAt: number;
}

interface LocalDBOptions {
	dbName?: string;
	storeName?: string;
	version?: number;
}

export class LocalDB<C extends CollectionsSchema> {
	private dbName: string;
	private storeName: string;
	private version: number;
	private schema: C;
	// Shared DB connection per (dbName, version, storeName)
	private static dbPromises = new Map<string, Promise<IDBDatabase>>();
	// Per-instance write chain to serialize write operations
	private writeChain: Promise<unknown> = Promise.resolve();

		constructor(schema: C, opts: LocalDBOptions = {}) {
		this.schema = schema;
		this.dbName = opts.dbName ?? 'mp-localdb';
		this.storeName = opts.storeName ?? 'kv';
		this.version = opts.version ?? 1;
	}

	// ---- IndexedDB helpers ----
		private getDb(): Promise<IDBDatabase> {
			const key = `${this.dbName}::${this.version}::${this.storeName}`;
			const existing = LocalDB.dbPromises.get(key);
			if (existing) return existing;
			const p = new Promise<IDBDatabase>((resolve, reject) => {
				const req = indexedDB.open(this.dbName, this.version);
				req.onupgradeneeded = () => {
					const db = req.result;
					if (!db.objectStoreNames.contains(this.storeName)) {
						const store = db.createObjectStore(this.storeName, { keyPath: 'key' });
						store.createIndex('by_collection', 'collection', { unique: false });
					}
				};
				req.onsuccess = () => resolve(req.result);
				req.onerror = () => reject(req.error);
			});
			LocalDB.dbPromises.set(key, p);
			return p;
		}

	private async tx(mode: IDBTransactionMode): Promise<IDBObjectStore> {
		const db = await this.getDb();
		return db.transaction(this.storeName, mode).objectStore(this.storeName);
	}

	private assertCollection(name: CollectionName): asserts name is keyof C & string {
		if (!this.schema || !Object.prototype.hasOwnProperty.call(this.schema, name)) {
			const known = Object.keys(this.schema).join(', ');
			throw new Error(`LocalDB: Unknown collection "${name}". Known: [${known}]`);
		}
	}

	private composeKey(collection: string, userKey: string): string {
		return `${collection}:${userKey}`;
	}

		private assertKey(collection: CollectionName, userKey: string): void {
			this.assertCollection(collection);
			const keys = (this.schema[collection] as any).keys as Record<string, unknown>;
			if (!keys || !Object.prototype.hasOwnProperty.call(keys, userKey)) {
				const known = Object.keys(keys || {}).join(', ');
				throw new Error(`LocalDB: Unknown key "${userKey}" for collection "${collection}". Known: [${known}]`);
			}
		}

	// ---- Public API ----

		async get<T extends JsonValue = JsonValue>(collection: CollectionName, userKey: string): Promise<T | null> {
			this.assertKey(collection, userKey);
		const store = await this.tx('readonly');
		const key = this.composeKey(collection, userKey);
		const rec = await new Promise<KVRecord | null>((resolve, reject) => {
			const req = store.get(key);
			req.onsuccess = () => resolve((req.result as KVRecord) || null);
			req.onerror = () => reject(req.error);
		});
		return (rec?.value as T) ?? null;
	}

		async set(collection: CollectionName, userKey: string, value: JsonValue): Promise<void> {
			this.assertKey(collection, userKey);
			await this.enqueueWrite(async () => {
				const store = await this.tx('readwrite');
				const now = Date.now();
				const rec: KVRecord = {
					key: this.composeKey(collection, userKey),
					collection,
					userKey,
					value,
					updatedAt: now,
				};
				await new Promise<void>((resolve, reject) => {
					const req = store.put(rec);
					req.onsuccess = () => resolve();
					req.onerror = () => reject(req.error);
				});
			});
	}

		async delete(collection: CollectionName, userKey: string): Promise<void> {
			this.assertKey(collection, userKey);
			await this.enqueueWrite(async () => {
				const store = await this.tx('readwrite');
				const key = this.composeKey(collection, userKey);
				await new Promise<void>((resolve, reject) => {
					const req = store.delete(key);
					req.onsuccess = () => resolve();
					req.onerror = () => reject(req.error);
				});
			});
	}

		async clear(collection?: CollectionName): Promise<void> {
			await this.enqueueWrite(async () => {
				const store = await this.tx('readwrite');
				if (!collection) {
					await new Promise<void>((resolve) => {
						const req = store.clear();
						req.onsuccess = () => resolve();
						req.onerror = () => resolve();
					});
					return;
				}
				this.assertCollection(collection);
				const index = store.index('by_collection');
				await new Promise<void>((resolve) => {
					const req = index.openKeyCursor(IDBKeyRange.only(collection));
					req.onsuccess = () => {
						const cursor = req.result as IDBCursor | null;
						if (cursor) {
							const key = cursor.primaryKey as string;
							const delReq = store.delete(key);
							delReq.onsuccess = () => cursor.continue();
							delReq.onerror = () => cursor.continue();
						} else {
							resolve();
						}
					};
					req.onerror = () => resolve();
				});
			});
		}

		// Serialize write operations with a simple promise chain
		private enqueueWrite<T>(work: () => Promise<T>): Promise<T> {
			const run = () => work().catch((e) => { throw e; });
			const next = this.writeChain.then(run, run) as Promise<T>;
			// Ensure chain continues regardless of success/failure
			this.writeChain = next.then(
				() => undefined,
				() => undefined
			);
			return next;
		}

		listCollections(): Array<{ name: keyof C & string; description?: string }> {
			return Object.entries(this.schema).map(([name, def]) => ({ name: name as keyof C & string, description: (def as any).description }));
	}

	async getAllInCollection<T extends JsonValue = JsonValue>(collection: CollectionName): Promise<Record<string, T>> {
		this.assertCollection(collection);
		const store = await this.tx('readonly');
		const index = store.index('by_collection');
		const out: Record<string, T> = {};
		await new Promise<void>((resolve) => {
			const req = index.openCursor(IDBKeyRange.only(collection));
			req.onsuccess = () => {
				const cursor = req.result as IDBCursorWithValue | null;
				if (cursor) {
					const rec = cursor.value as KVRecord;
					out[rec.userKey] = rec.value as T;
					cursor.continue();
				} else {
					resolve();
				}
			};
			req.onerror = () => resolve();
		});
		return out;
	}

	async exportAll(): Promise<Record<string, Record<string, JsonValue>>> {
		const store = await this.tx('readonly');
		const out: Record<string, Record<string, JsonValue>> = {};
		await new Promise<void>((resolve) => {
			const req = store.openCursor();
			req.onsuccess = () => {
				const cursor = req.result as IDBCursorWithValue | null;
				if (cursor) {
					const rec = cursor.value as KVRecord;
					out[rec.collection] = out[rec.collection] || {};
					out[rec.collection][rec.userKey] = rec.value;
					cursor.continue();
				} else {
					resolve();
				}
			};
			req.onerror = () => resolve();
		});
		return out;
	}
}

// Default LocalDB instance with statically defined schema (namespaces and keys)
export const localDB = new LocalDB(localDBSchema);

export default localDB;